/**
 * ServiceTickets — reactive repairs & service calls board.
 * Property-linked tickets with status pipeline, field photos, T&M invoicing
 * and one-click convert-to-deal when a call turns into a real job.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Receipt, ArrowUpRight, Camera, X, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Scheduled", "On Site", "Complete", "Invoiced", "Converted"];
const ISSUE_TYPES = ["Leak", "Ponding", "Storm Damage", "Warranty", "Penetration/HVAC", "Inspection", "Other"];
const PRIORITIES = ["Low", "Normal", "High", "Emergency"];

const STATUS_STYLES = {
  New: "bg-blue-100 text-blue-800 border-blue-300",
  Scheduled: "bg-violet-100 text-violet-800 border-violet-300",
  "On Site": "bg-amber-100 text-amber-800 border-amber-300",
  Complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Invoiced: "bg-zinc-200 text-zinc-700 border-zinc-300",
  Converted: "bg-cyan-100 text-cyan-800 border-cyan-300",
};
const PRIORITY_STYLES = {
  Low: "text-zinc-500",
  Normal: "text-zinc-700",
  High: "text-orange-600 font-bold",
  Emergency: "text-red-600 font-black",
};

// Templates come from Settings → Service Templates (db-backed, user-editable).
// Placeholders merged from the ticket at use time.
const mergeTpl = (text, t) => (text || "")
  .replaceAll("{ticket_number}", t.ticket_number || "")
  .replaceAll("{property_name}", t.property_name || "")
  .replaceAll("{description}", t.description || "")
  .replaceAll("{resolution_notes}", t.resolution_notes || t.description || "")
  .replaceAll("{reported_by}", t.reported_by || "")
  .replaceAll("{issue_type}", t.issue_type || "");

const empty = {
  property_id: "", contact_id: "", reported_by: "", reported_phone: "",
  issue_type: "Leak", priority: "Normal", status: "New", description: "",
  assigned_to_user_id: "", scheduled_date: "", resolution_notes: "",
  labor_hours: "", labor_rate: "", materials_cost: "",
};

const inputCls = "w-full h-10 px-3 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1";

export default function ServiceTickets() {
  const { user } = useAuth();
  const isSales = user?.role === "sales";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Open");
  const [propertyFilter, setPropertyFilter] = useState(searchParams.get("property_id") || "");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState("");
  const [templates, setTemplates] = useState([]);
  // Invoice verbiage dialog
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState("");
  const [invoiceDesc, setInvoiceDesc] = useState("");
  // Findings email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailForm, setEmailForm] = useState({ to_email: "", cc_email: "", subject: "", body: "", attach_invoice: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const invoiceTemplates = templates.filter((t) => t.kind === "invoice");
  const emailTemplates = templates.filter((t) => t.kind === "email");

  const load = () => api.get("/service-tickets").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data || []));
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/service-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const loadPhotos = (ticketId) =>
    api.get(`/files?parent_type=service_ticket&parent_id=${ticketId}`).then((r) => setPhotos(r.data || [])).catch(() => setPhotos([]));

  const openNew = () => { setEditing(null); setForm({ ...empty, property_id: propertyFilter || "" }); setPhotos([]); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, t[k] ?? ""])) });
    loadPhotos(t.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      labor_hours: parseFloat(form.labor_hours) || 0,
      labor_rate: parseFloat(form.labor_rate) || 0,
      materials_cost: parseFloat(form.materials_cost) || 0,
    };
    try {
      if (editing) {
        await api.patch(`/service-tickets/${editing.id}`, payload);
        toast.success("Ticket updated");
      } else {
        const r = await api.post("/service-tickets", payload);
        setEditing(r.data);
        toast.success(`Ticket ${r.data.ticket_number} created`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (t, status) => {
    await api.patch(`/service-tickets/${t.id}`, { status });
    load();
  };

  const remove = async () => {
    await api.delete(`/service-tickets/${confirmTarget.id}`);
    setConfirmTarget(null);
    toast.success("Ticket deleted");
    load();
  };

  const uploadPhoto = async (file) => {
    if (!editing || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("parent_type", "service_ticket");
      fd.append("parent_id", editing.id);
      fd.append("category", "Photo");
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      loadPhotos(editing.id);
      toast.success("Photo added");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const pickDefault = (list) => {
    // Warranty tickets default to a "warrant"-named template when one exists
    if (editing?.issue_type === "Warranty") {
      const w = list.find((t) => /warrant/i.test(t.name));
      if (w) return w;
    }
    return list[0] || null;
  };

  const openInvoiceDialog = () => {
    const tpl = pickDefault(invoiceTemplates);
    setInvoiceTemplate(tpl?.id || "");
    setInvoiceDesc(tpl ? mergeTpl(tpl.body, editing) : "");
    setInvoiceOpen(true);
  };

  const makeInvoice = async () => {
    setActing("invoice");
    try {
      const r = await api.post("/invoices/from-service-ticket", { ticket_id: editing.id, description: invoiceDesc });
      toast.success(`Draft invoice ${r.data.invoice_number} created`);
      setInvoiceOpen(false);
      setOpen(false);
      load();
      navigate("/invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invoice");
    } finally {
      setActing("");
    }
  };

  const applyEmailTemplate = (id, base = emailForm) => {
    setEmailTemplate(id);
    const tpl = emailTemplates.find((t) => t.id === id);
    if (!tpl) return;
    setEmailForm({ ...base, subject: mergeTpl(tpl.subject, editing), body: mergeTpl(tpl.body, editing) });
  };

  const openEmailDialog = async () => {
    let to = "";
    try {
      const prop = properties.find((p) => p.id === editing.property_id);
      if (prop?.property_contact_id) {
        const c = await api.get(`/contacts/${prop.property_contact_id}`);
        to = c.data.billing_contact_email || c.data.email || "";
      }
    } catch (err) { /* prefill only */ }
    const base = { to_email: to, cc_email: "", subject: "", body: "", attach_invoice: !!editing.invoice_id };
    const tpl = pickDefault(emailTemplates);
    setEmailTemplate(tpl?.id || "");
    setEmailForm(tpl ? { ...base, subject: mergeTpl(tpl.subject, editing), body: mergeTpl(tpl.body, editing) } : base);
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const r = await api.post(`/service-tickets/${editing.id}/email`, emailForm);
      toast.success(r.data.message || "Email sent");
      setEmailOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email failed to send");
    } finally {
      setSendingEmail(false);
    }
  };

  const convertToDeal = async () => {
    setActing("convert");
    try {
      const r = await api.post(`/service-tickets/${editing.id}/convert-to-deal`);
      toast.success("Deal created from ticket");
      navigate(`/projects/${r.data.id}`);
    } catch (err) {
      toast.error("Could not convert to deal");
    } finally {
      setActing("");
    }
  };

  const token = localStorage.getItem("crm_token") || "";
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name || u.email])), [users]);

  const filtered = items.filter((t) => {
    if (propertyFilter && t.property_id !== propertyFilter) return false;
    if (statusFilter === "All") return true;
    if (statusFilter === "Open") return ["New", "Scheduled", "On Site"].includes(t.status);
    return t.status === statusFilter;
  });
  const countFor = (f) =>
    items.filter((t) => (!propertyFilter || t.property_id === propertyFilter))
      .filter((t) => (f === "All" ? true : f === "Open" ? ["New", "Scheduled", "On Site"].includes(t.status) : t.status === f)).length;

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="service-tickets-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Service Calls</h1>
          <p className="text-sm text-zinc-500 mt-1">Repairs, leaks and reactive calls — tracked per building</p>
        </div>
        <button data-testid="new-ticket-button" onClick={openNew} className="inline-flex items-center gap-2 h-10 px-4 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm transition-colors">
          <Plus className="w-4 h-4" /> New Ticket
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {["Open", ...STATUSES, "All"].map((s) => (
          <button
            key={s}
            data-testid={`ticket-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 h-8 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors ${
              statusFilter === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"
            }`}
          >
            {s} ({countFor(s)})
          </button>
        ))}
        <select
          data-testid="ticket-property-filter"
          value={propertyFilter}
          onChange={(e) => setPropertyFilter(e.target.value)}
          className="ml-auto h-8 px-2 border border-zinc-300 bg-white rounded-sm text-xs focus:outline-none"
        >
          <option value="">All Properties</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.property_name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">No service tickets{statusFilter !== "All" ? ` (${statusFilter})` : ""}. Log one with New Ticket.</div>
        ) : (
          <table className="w-full text-sm" data-testid="tickets-table">
            <thead>
              <tr className="border-b-2 border-zinc-950 text-left">
                {["Ticket", "Property", "Issue", "Priority", "Reported By", "Assigned", "Scheduled", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`ticket-row-${t.id}`} onClick={() => openEdit(t)}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-blue-700">{t.ticket_number}</td>
                  <td className="px-4 py-3 font-bold text-zinc-950">{t.property_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{t.issue_type}</td>
                  <td className={`px-4 py-3 text-xs uppercase tracking-wider ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{t.reported_by || "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{usersById[t.assigned_to_user_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{t.scheduled_date || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      data-testid={`ticket-status-${t.id}`}
                      value={t.status}
                      onChange={(e) => quickStatus(t, e.target.value)}
                      className={`text-[10px] font-bold uppercase tracking-wider border rounded-sm px-1.5 h-7 ${STATUS_STYLES[t.status] || "bg-white"}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`edit-ticket-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 hover:bg-zinc-200 rounded-sm"><Pencil className="w-3.5 h-3.5" /></button>
                      {!isSales && (
                        <button data-testid={`delete-ticket-${t.id}`} onClick={() => setConfirmTarget(t)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing ? `${editing.ticket_number} — ${editing.property_name || "Service Ticket"}` : "New Service Ticket"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="ticket-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Property *</label>
                <select data-testid="ticket-property" required className={inputCls} value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}>
                  <option value="">Select building…</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.property_name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Assigned Tech</label>
                <select data-testid="ticket-assignee" className={inputCls} value={form.assigned_to_user_id} onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })}>
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Reported By</label>
                <input data-testid="ticket-reported-by" className={inputCls} value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} placeholder="Tenant, PM, owner…" />
              </div>
              <div>
                <label className={labelCls}>Callback Phone</label>
                <input data-testid="ticket-reported-phone" className={inputCls} value={form.reported_phone} onChange={(e) => setForm({ ...form, reported_phone: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Issue Type</label>
                <select data-testid="ticket-issue-type" className={inputCls} value={form.issue_type} onChange={(e) => setForm({ ...form, issue_type: e.target.value })}>
                  {ISSUE_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Priority</label>
                <select data-testid="ticket-priority" className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select data-testid="ticket-status-field" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Scheduled Date</label>
                <input data-testid="ticket-scheduled-date" type="date" className={inputCls} value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Issue Description</label>
              <textarea data-testid="ticket-description" rows={3} className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Leak over suite 210 near HVAC unit…" />
            </div>
            <div>
              <label className={labelCls}>Field / Resolution Notes</label>
              <textarea data-testid="ticket-resolution" rows={2} className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700" value={form.resolution_notes} onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })} />
            </div>

            <div className="grid grid-cols-3 gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
              <div>
                <label className={labelCls}>Labor Hours</label>
                <input data-testid="ticket-labor-hours" type="number" min="0" step="0.5" className={inputCls} value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Labor Rate ($/hr)</label>
                <input data-testid="ticket-labor-rate" type="number" min="0" step="1" className={inputCls} value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Materials ($)</label>
                <input data-testid="ticket-materials-cost" type="number" min="0" step="0.01" className={inputCls} value={form.materials_cost} onChange={(e) => setForm({ ...form, materials_cost: e.target.value })} />
              </div>
            </div>

            {editing && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls + " mb-0"}>Field Photos ({photos.length})</label>
                  <label className="inline-flex items-center gap-1.5 px-3 h-8 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 rounded-sm cursor-pointer hover:border-zinc-950">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Add Photo
                    <input data-testid="ticket-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  </label>
                </div>
                {photos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((f) => (
                      <a key={f.id} href={`${API}/files/${f.id}/download?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" className="block border border-zinc-200 rounded-sm overflow-hidden hover:border-blue-700">
                        <img src={`${API}/files/${f.id}/download?token=${encodeURIComponent(token)}`} alt={f.original_filename} className="w-full h-20 object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-200">
              <button type="submit" disabled={saving} data-testid="save-ticket-button" className="inline-flex items-center gap-2 h-10 px-5 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save Changes" : "Create Ticket"}
              </button>
              {editing && !["Invoiced", "Converted"].includes(editing.status) && (
                <>
                  <button type="button" onClick={openInvoiceDialog} disabled={!!acting} data-testid="ticket-invoice-button" className="inline-flex items-center gap-1.5 h-10 px-4 border border-emerald-600 text-emerald-700 text-xs font-bold uppercase tracking-wider hover:bg-emerald-50 rounded-sm disabled:opacity-50">
                    {acting === "invoice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Create Invoice
                  </button>
                  <button type="button" onClick={convertToDeal} disabled={!!acting} data-testid="ticket-convert-button" className="inline-flex items-center gap-1.5 h-10 px-4 border border-zinc-950 text-zinc-950 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 rounded-sm disabled:opacity-50">
                    {acting === "convert" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />} Convert to Deal
                  </button>
                </>
              )}
              {editing && (
                <button type="button" onClick={openEmailDialog} data-testid="ticket-email-button" className="inline-flex items-center gap-1.5 h-10 px-4 border border-blue-700 text-blue-700 text-xs font-bold uppercase tracking-wider hover:bg-blue-50 rounded-sm">
                  <Mail className="w-4 h-4" /> Findings Email
                </button>
              )}
              {editing?.reported_phone && (
                <a href={`tel:${editing.reported_phone}`} className="inline-flex items-center gap-1.5 h-10 px-3 text-xs text-zinc-600 hover:text-blue-700"><Phone className="w-3.5 h-3.5" /> {editing.reported_phone}</a>
              )}
              <button type="button" onClick={() => setOpen(false)} className="ml-auto inline-flex items-center gap-1 h-10 px-3 text-xs text-zinc-500 hover:text-zinc-950"><X className="w-4 h-4" /> Close</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice verbiage dialog — pick + edit the line description before creating */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">Invoice Verbiage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Template</label>
              <select
                data-testid="invoice-verbiage-template"
                className={inputCls}
                value={invoiceTemplate}
                onChange={(e) => {
                  setInvoiceTemplate(e.target.value);
                  const tpl = invoiceTemplates.find((t) => t.id === e.target.value);
                  if (tpl) setInvoiceDesc(mergeTpl(tpl.body, editing));
                }}
              >
                {invoiceTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <div className="text-[10px] text-zinc-400 mt-1">Manage templates in Settings → Service Templates</div>
            </div>
            <div>
              <label className={labelCls}>Invoice Line Description (edit freely)</label>
              <textarea data-testid="invoice-verbiage-text" rows={6} className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700" value={invoiceDesc} onChange={(e) => setInvoiceDesc(e.target.value)} />
            </div>
            {editing && (
              <div className="text-xs text-zinc-500">
                Billing: {Number(editing.labor_hours) > 0 ? `${editing.labor_hours} hr × $${Number(editing.labor_rate).toLocaleString()}` : "no labor"}{Number(editing.materials_cost) > 0 ? ` + $${Number(editing.materials_cost).toLocaleString()} materials` : ""} — amounts come from the ticket's T&M fields.
              </div>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
              <button onClick={makeInvoice} disabled={acting === "invoice" || !invoiceDesc.trim()} data-testid="invoice-verbiage-create" className="inline-flex items-center gap-2 h-10 px-5 bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-800 rounded-sm disabled:opacity-50">
                {acting === "invoice" && <Loader2 className="w-4 h-4 animate-spin" />} Create Draft Invoice
              </button>
              <button onClick={() => setInvoiceOpen(false)} className="h-10 px-3 text-xs text-zinc-500 hover:text-zinc-950">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Findings email dialog — template + fully editable before send */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">Findings Email {editing ? `— ${editing.ticket_number}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Template</label>
              <select
                data-testid="email-template-select"
                className={inputCls}
                value={emailTemplate}
                onChange={(e) => applyEmailTemplate(e.target.value)}
              >
                {emailTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <div className="text-[10px] text-zinc-400 mt-1">Manage templates in Settings → Service Templates</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>To *</label>
                <input data-testid="email-to" type="email" className={inputCls} value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>CC</label>
                <input data-testid="email-cc" type="email" className={inputCls} value={emailForm.cc_email} onChange={(e) => setEmailForm({ ...emailForm, cc_email: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Subject</label>
              <input data-testid="email-subject" className={inputCls} value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Message (edit freely)</label>
              <textarea data-testid="email-body" rows={12} className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 font-mono" value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="email-attach-invoice" type="checkbox" disabled={!editing?.invoice_id} checked={emailForm.attach_invoice && !!editing?.invoice_id} onChange={(e) => setEmailForm({ ...emailForm, attach_invoice: e.target.checked })} />
              Attach invoice PDF {editing?.invoice_id ? "" : "(no invoice on this ticket yet)"}
            </label>
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
              <button onClick={sendEmail} disabled={sendingEmail || !emailForm.to_email.trim() || !emailForm.body.trim()} data-testid="email-send-button" className="inline-flex items-center gap-2 h-10 px-5 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm disabled:opacity-50">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Email
              </button>
              <button onClick={() => setEmailOpen(false)} className="h-10 px-3 text-xs text-zinc-500 hover:text-zinc-950">Cancel</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        title="Delete this ticket?"
        message={`${confirmTarget?.ticket_number || ""} will be removed. This cannot be undone.`}
        onConfirm={remove}
      />
    </div>
  );
}
